
import type { Id, JobStatus, TaskStatus } from '../types/api';
import { isTaskFinished, TERMINAL_JOB_STATUSES } from '../types/api';
import type {
  CharactersListParams,
  InsertListParams,
  PageParams,
  FontsListParams,
  PromptsListParams,
  ActivityParams,
  SubtitleBatchesParams,
  TranslateRunsParams,
  TtsRunsParams,
  VoicesListParams,
} from '../api/endpoints';

export const POLL_INTERVAL_MS = 2500;

export function pollIntervalFrom(pollAfterSeconds: number | undefined): number {
  if (!pollAfterSeconds || pollAfterSeconds <= 0) return POLL_INTERVAL_MS;
  return Math.max(1000, Math.round(pollAfterSeconds * 1000));
}

export function shouldPoll(status: JobStatus | undefined): boolean {
  if (!status) return true;
  return !TERMINAL_JOB_STATUSES.includes(status);
}

export function pollWhileTaskRuns(
  status: TaskStatus | undefined,
  pollAfterSeconds?: number,
): number | false {
  if (isTaskFinished(status)) return false;
  return pollIntervalFrom(pollAfterSeconds);
}

export const queryKeys = {
  me: ['me'] as const,
  health: ['health'] as const,

  jobArtifacts: (jobId: Id) => ['job', jobId, 'artifacts'] as const,
  artifactUrl: (artifactId: Id) => ['artifact-url', artifactId] as const,

  tasksRoot: ['tool-tasks'] as const,
  tasks: (kind: string | undefined) => ['tool-tasks', 'list', kind ?? null] as const,
  task: (taskId: Id) => ['tool-tasks', 'item', taskId] as const,

  ttsRoot: ['tts'] as const,
  ttsProviderVoices: ['tts', 'provider-voices'] as const,
  ttsRuns: (params: TtsRunsParams) => ['tts', 'runs', params] as const,
  ttsRun: (runId: Id) => ['tts', 'run', runId] as const,

  libraryRoot: ['library'] as const,
  inserts: (params: InsertListParams) => ['library', 'inserts', params] as const,
  insert: (itemId: Id) => ['library', 'insert', itemId] as const,
  insertSearch: (payload: unknown) => ['library', 'search', payload] as const,
  libraryFacets: (limit: number) => ['library', 'facets', limit] as const,

  cutRoot: ['cut'] as const,
  cutRun: (runId: Id) => ['cut', 'run', runId] as const,

  charactersRoot: ['characters'] as const,
  characters: (params: CharactersListParams) => ['characters', 'list', params] as const,
  character: (characterId: Id) => ['characters', 'item', characterId] as const,
  characterAvatarUrl: (characterId: Id) => ['characters', 'avatar-url', characterId] as const,
  characterJobs: (characterId: Id, params: PageParams) =>
    ['characters', 'jobs', characterId, params] as const,

  translateRoot: ['translate'] as const,
  translateRuns: (params: TranslateRunsParams) => ['translate', 'runs', params] as const,
  translateRun: (runId: Id) => ['translate', 'run', runId] as const,

  subtitlesRoot: ['subtitles'] as const,
  subtitlePresets: ['subtitles', 'presets'] as const,
  subtitleSettings: ['subtitles', 'settings'] as const,
  subtitleBatches: (params: SubtitleBatchesParams) => ['subtitles', 'batches', params] as const,

  videoGenRoot: ['video-gen'] as const,
  videoGenStatus: ['video-gen', 'status'] as const,
  videoGenList: (params: PageParams) => ['video-gen', 'list', params] as const,

  fontsRoot: ['fonts'] as const,
  fonts: (params: FontsListParams) => ['fonts', 'list', params] as const,
  fontTeams: ['fonts', 'teams'] as const,
  promptsRoot: ['prompts'] as const,
  prompts: (params: PromptsListParams) => ['prompts', 'list', params] as const,

  voicesRoot: ['voices'] as const,
  voices: (params: VoicesListParams) => ['voices', 'list', params] as const,
  voice: (voiceId: Id) => ['voices', 'item', voiceId] as const,
  voiceSample: (voiceId: Id) => ['voices', 'sample', voiceId] as const,
  voiceImportSpec: ['voices', 'import-spec'] as const,

  adminUsers: ['admin', 'users'] as const,
  adminTeams: ['admin', 'teams'] as const,
  adminActivity: (params: ActivityParams) => ['admin', 'activity', params] as const,
  adminActivityFilters: ['admin', 'activity', 'filters'] as const,
};
